import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Add, EditOutlined, DeleteOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { warehouseService } from '../../warehouses';
import WarehouseModalForm from '../../warehouses/components/WarehouseModalForm';
import { companyService } from '../index';

const CompanyWarehouses = ({ companyId }) => {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    loadWarehouses();
    loadCompanies();
  }, [companyId]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const response = await warehouseService.getAll({ companyId, limit: 100 });
      setWarehouses(response.data);
    } catch (error) {
      showSnackbar('Error al cargar bodegas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedWarehouse(null);
    setOpenModal(true);
  };

  const handleEdit = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setOpenModal(true);
  };

  const handleSave = async (data) => {
    try {
      if (selectedWarehouse) {
        await warehouseService.update(selectedWarehouse.id, data);
        showSnackbar('Bodega actualizada exitosamente', 'success');
      } else {
        await warehouseService.create({ ...data, companyId: parseInt(companyId) });
        showSnackbar('Bodega creada exitosamente', 'success');
      }
      setOpenModal(false);
      loadWarehouses();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar bodega', 'error');
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta bodega?')) {
      try {
        await warehouseService.delete(id);
        showSnackbar('Bodega eliminada exitosamente', 'success');
        loadWarehouses();
      } catch (error) {
        showSnackbar('Error al eliminar bodega', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Bodegas de Archivo</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Nueva Bodega
        </Button>
      </Box>

      {warehouses.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No hay bodegas registradas</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Direccion</TableCell>
                <TableCell>Cajas</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {warehouses.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((warehouse) => (
                <TableRow
                  key={warehouse.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/warehouses/${warehouse.id}`)}
                >
                  <TableCell>{warehouse.name}</TableCell>
                  <TableCell>{warehouse.address || '—'}</TableCell>
                  <TableCell>{warehouse._count?.box_warehouse || 0}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                      <IconButton sx={{ color: '#5d5d60', width: 36, height: 36 }} onClick={(e) => { e.stopPropagation(); handleEdit(warehouse); }}>
                        <EditOutlined sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton sx={{ color: '#5d5d60', width: 36, height: 36 }} onClick={(e) => { e.stopPropagation(); handleDelete(warehouse.id); }}>
                        <DeleteOutlined sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <WarehouseModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        warehouse={selectedWarehouse}
        companies={companies}
        preselectedCompanyId={companyId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyWarehouses;
